import type { TaskSummary } from "@/api/tasks";

/** A workflow step counts as "done" across the synonyms svc-07 may emit.
 * Kept in lock-step with the tasks-table WorkflowChips (TasksModal) so the
 * header red dot and the per-task chips can never disagree (PENN-075). */
function isWorkflowDone(status: string | null | undefined): boolean {
  const v = (status ?? "").toLowerCase();
  return (
    v === "done" ||
    v === "approved" ||
    v === "complete" ||
    v === "completed" ||
    v === "quoted"
  );
}

/**
 * Drives the header "new task" red dot. PENN-075: the dot must mirror the
 * tasks-table chips exactly — a viewable task is PENDING (dot on) until BOTH
 * its Engineering Review AND Quoted chips are green. Previously the dot was
 * cleared as soon as the quote step completed, so a quoted task whose
 * engineering review was still pending wrongly hid the dot even though the
 * table showed it as unfinished.
 *
 * Rules (mirror WorkflowChips):
 * - closed / archived → not pending
 * - sales-flow tasks have no eng-review step → only the Quoted chip counts
 * - otherwise pending unless eng-review AND quote are both done
 */
export function isTaskPending(t: TaskSummary): boolean {
  const status = (t.status ?? "open").toLowerCase();

  if (status === "closed" || status === "archived") return false;

  // Sales-flow tasks have no engineering-review step (bug-061 / R-4), so that
  // column can never complete for them — only the quote step counts.
  const engDone =
    t.flowType === "sales" || isWorkflowDone(t.workflowEngReviewStatus);
  const quoteDone =
    isWorkflowDone(t.workflowQuoteStatus) ||
    !!t.quotedAt ||
    status === "quoted";
  return !(engDone && quoteDone);
}

/** True when any task in the list still needs attention (drives the red dot). */
export function hasPendingTask(tasks: readonly TaskSummary[] | undefined): boolean {
  return (tasks ?? []).some(isTaskPending);
}
